import { useMemo, useState } from "react";
import { apiRoutes, type RouteEntry } from "./architecture";

const METHOD_TONE: Record<RouteEntry["method"], string> = {
  GET: "secondary",
  POST: "primary",
  PUT: "tertiary",
  PATCH: "tertiary",
  DELETE: "error",
};

export function AboutApi() {
  const [q, setQ] = useState("");
  const [methods, setMethods] = useState<Record<string, boolean>>({});

  const grouped = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const filtered = apiRoutes.filter((r) => {
      if (Object.values(methods).some(Boolean) && !methods[r.method]) return false;
      if (!needle) return true;
      return r.path.toLowerCase().includes(needle) || r.description.toLowerCase().includes(needle);
    });
    const map = new Map<string, RouteEntry[]>();
    for (const r of filtered) {
      if (!map.has(r.resource)) map.set(r.resource, []);
      map.get(r.resource)!.push(r);
    }
    return Array.from(map.entries());
  }, [q, methods]);

  const allMethods: RouteEntry["method"][] = ["GET", "POST", "PUT", "PATCH", "DELETE"];

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <div className="card" style={{ padding: 16, display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
        <input
          type="search"
          placeholder="Search routes…"
          aria-label="Search routes"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            flex: "1 1 200px", height: 36, padding: "0 12px",
            borderRadius: "var(--r-pill)", border: "1px solid var(--md-outline-var)",
            background: "var(--md-sc-lowest)", color: "var(--md-on-surface)",
            font: "var(--t-body-md)",
          }}
        />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }} role="group" aria-label="Method filter">
          {allMethods.map((m) => {
            const on = !!methods[m];
            return (
              <button
                key={m}
                type="button"
                aria-pressed={on}
                onClick={() => setMethods((s) => ({ ...s, [m]: !s[m] }))}
                className="lex-chip"
                data-tone={on ? METHOD_TONE[m] : undefined}
                style={{ border: on ? "none" : "1px solid var(--md-outline-var)", cursor: "pointer" }}
              >
                {m}
              </button>
            );
          })}
        </div>
      </div>

      {grouped.length === 0 && (
        <div className="card" style={{ padding: 28, textAlign: "center", color: "var(--md-on-surface-var)" }}>
          No routes match those filters.
        </div>
      )}

      {grouped.map(([resource, routes]) => (
        <section key={resource} className="card" style={{ padding: 20 }}>
          <div className="lex-eyebrow">RESOURCE</div>
          <h2 style={{ font: "var(--t-headline-sm)", fontStyle: "italic", marginTop: 0, marginBottom: 12 }}>{resource}</h2>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={th}>Method</th>
                <th style={th}>Path</th>
                <th style={th}>Description</th>
              </tr>
            </thead>
            <tbody>
              {routes.map((r) => (
                <tr key={`${r.method}-${r.path}`} style={{ borderTop: "1px solid var(--md-outline-var)" }}>
                  <td style={td}>
                    <span className="lex-chip" data-tone={METHOD_TONE[r.method]} data-size="sm">{r.method}</span>
                  </td>
                  <td style={{ ...td, font: "var(--t-mono-sm)" }}>{r.path}</td>
                  <td style={{ ...td, color: "var(--md-on-surface-var)" }}>{r.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}
    </div>
  );
}

const th = { textAlign: "left" as const, padding: "10px 12px", font: "var(--t-label-md)", color: "var(--md-on-surface-var)" };
const td = { padding: "10px 12px", font: "var(--t-body-md)" };

export default AboutApi;
